import React from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import { getFileLinks } from '../../../api/emailsApi/files';
import { changeAlertText } from '../../actions/index';
import { navigatingFolders } from '../../actions/folders';
import officeApi from '../../../api/officeApi';
import { handleDocrioAttachments, calculateAttachmentHash, isWindowsDesktop } from '../../utils/browser';
import * as RootActions from '../../actions/index';
import * as FileActions from '../../actions/files';
import { navigatingDocrioFiles } from '../../actions/files';
import axios from 'axios';
import { Buffer } from 'buffer/';
import './Attach.scss';

/**
 * The URL-based method (addFileAttachmentAsync) is known to fail for larger files (~10MB+)
 * in Outlook on the web/new Outlook, so known-large files skip straight to uploading content.
 * @type {number}
 */
const URL_ATTACH_SIZE_LIMIT_BYTES = 10 * 1024 * 1024;

/**
 * addFileAttachmentFromBase64Async uses platform-specific caps for base64 string length.
 * Base64 encodes 3 raw bytes as 4 characters, so each platform's byte cap is derived from its
 * character cap using floor(charCap / 4) * 3.
 * @type {number}
 */
const BASE64_CHAR_CAP_WEB_WINDOWS = 34865152;
const BASE64_CHAR_CAP_MAC_MOBILE = 27892122;

const HIGH_BASE64_CAP_PLATFORMS = new Set(['pc', 'officeonline']);

const getMaxBase64AttachmentSizeBytes = (platform) => {
  const normalizedPlatform = typeof platform === 'string' ? platform.toLowerCase() : '';
  const base64CharCap = HIGH_BASE64_CAP_PLATFORMS.has(normalizedPlatform) ? BASE64_CHAR_CAP_WEB_WINDOWS : BASE64_CHAR_CAP_MAC_MOBILE;
  return Math.floor(base64CharCap / 4) * 3;
};

const getFileTooLargeErrorMessage = (maxBase64AttachmentSizeBytes) => {
  const maxSizeMb = Math.round(maxBase64AttachmentSizeBytes / (1024 * 1024));
  return `File is too large to attach (max ~${maxSizeMb}MB on this platform).`;
};

export const Attach = () => {
  const dispatch = useDispatch();
  const selectedFiles = useSelector((state) => state.files.selectedFiles);

  const formatAttachmentsForWeb = (responses) => {
    const docrioAttachments = {};
    responses.forEach((response) => {
      docrioAttachments[response.value] = response.id;
    });
    return docrioAttachments;
  };

  const formatAttachmentsForDesktop = async (responses) => {
    const docrioAttachments = {};
    await Promise.all(responses.map(async (response) => {
      const content = await officeApi.getAttachmentContent(response.value);
      const name = selectedFiles[response.id].name;
      const hash = calculateAttachmentHash(name, content.value.content);
      docrioAttachments[hash] = response.id;
    }));
    return docrioAttachments;
  };

  const onAttach = async () => {
    dispatch(RootActions.isLoading(true));
    const platform = officeApi.getDiagnosticsPlatform();
    const maxBase64AttachmentSizeBytes = getMaxBase64AttachmentSizeBytes(platform);
    const fileTooLargeErrorMessage = getFileTooLargeErrorMessage(maxBase64AttachmentSizeBytes);
    let attachmentLinks;
    try {
      attachmentLinks = await getFileLinks(Object.keys(selectedFiles));
    } catch (e) {
      dispatch(changeAlertText('Failed to get file links', 'error'));
      dispatch(RootActions.isLoading(false));
      return;
    }
    const request = axios;
    request.defaults.withCredentials = false;

    // Fetches the file content directly and attaches it as base64. Used upfront for known-large
    // files, and as a fallback when the URL-based method fails for files of unknown or underestimated size.
    const attachByContent = async (record) => {
      const config = {
        method: 'GET',
        url: record.SignedUrlV2,
        responseType: 'arraybuffer',
      };
      const s3Response = await request(config);
      const base64String = Buffer.from(s3Response.data, 'binary').toString('base64');
      return officeApi.putAttachmentContent(base64String, selectedFiles[record.Id].name);
    };

    // Wraps attachByContent so a thrown/rejected attach (e.g. Office.js's own size validation)
    // always resolves to a failed response instead of rejecting the whole Promise.all.
    const attachByContentSafely = async (record) => {
      try {
        return await attachByContent(record);
      } catch (attachError) {
        // Avoid logging the raw error since it includes the presigned SignedUrlV2 (with S3 credentials) in its request config.
        console.log('Attach by content failed', attachError?.message, attachError?.response?.status);
        return { status: 'failed', error: { message: attachError?.message } };
      }
    };

    const responses = await Promise.all(attachmentLinks?.Records?.map(async (record) => {
      let resp;
      const fileSize = selectedFiles[record.Id]?.size;
      const isKnownTooLargeForContent = typeof fileSize === 'number' && fileSize > maxBase64AttachmentSizeBytes;

      if (isKnownTooLargeForContent) {
        console.log(`${selectedFiles[record.Id].name} exceeds the max attachment size supported`, fileSize);
        resp = { status: 'failed', error: { message: fileTooLargeErrorMessage } };
      } else if (isWindowsDesktop()) {
        resp = await attachByContentSafely(record);
      } else {
        const isKnownLargeFile = typeof fileSize === 'number' && fileSize > URL_ATTACH_SIZE_LIMIT_BYTES;

        if (!isKnownLargeFile) {
          resp = await officeApi.putAttachments(record.SignedUrlV2, selectedFiles[record.Id].name);
        }

        // Known-large files skip straight here; others fall back here only when the URL-based attempt failed.
        if (isKnownLargeFile || resp.status === 'failed') {
          if (resp) {
            console.log('Attaching via URL failed, retrying by uploading file content directly', resp.error);
          }
          resp = await attachByContentSafely(record);
        }
        dispatch(FileActions.attachedFiles({}));
      }
      return { ...resp, id: record.Id };
    }));

    // check for failures
    const failures = responses.filter((response) => response.status === 'rejected' || response.status === 'failed');
    batch(async () => {
      if (failures.length > 0) {
        console.log('FAILED TO ATTACH', failures);
        const allTooLarge = failures.every((failure) => failure.error?.message === fileTooLargeErrorMessage);
        dispatch(changeAlertText(allTooLarge ? fileTooLargeErrorMessage : 'One or more files failed to attach.', 'error'));
      } else {
        const fileCount = Object.keys(selectedFiles).length;
        const alertText = `${fileCount} file${fileCount > 1 ? 's' : ''} successfully attached.`;
        dispatch(changeAlertText(alertText));
        // on web we can use graph ids but on desktop we need to calculate hashes.
        const docrioAttachments = isWindowsDesktop() ? await formatAttachmentsForDesktop(responses) : formatAttachmentsForWeb(responses);
        await handleDocrioAttachments(docrioAttachments);
      }
      dispatch(navigatingFolders(false));
      dispatch(navigatingDocrioFiles(false));
      dispatch(RootActions.isLoading(false));
    });
  };

  return (
    <div className='attach-component-container'>
      <div id='filesSelected' className='attach-component-files-selected'>
        Files Selected: {Object.keys(selectedFiles).length}
      </div>
      <div className='attach-component-button-container'>
        <button
          className='attach-component-button'
          onClick={onAttach}
          disabled={Object.keys(selectedFiles).length === 0}>
          Attach
        </button>
      </div>
    </div>
  );
};
